import { load } from '@fingerprintjs/fingerprintjs';
import type {
  FingerprintVideoCardInfo,
  IUserFingerprintData,
  UserFingerprintDataInstanceType,
} from 'services/FingerprintService/fingerprint.model';

export interface IGetUserDataDto {
  instance_id: string;
  instance_type: UserFingerprintDataInstanceType;
}

const getBattery = async (): Promise<boolean> => {
  if ('getBattery' in navigator) {
    return await (navigator.getBattery as any)().then((battery: any) => battery.level > 0);
  }

  return false;
};

function getVideoCardInfo(): FingerprintVideoCardInfo {
  const gl = document.createElement('canvas').getContext('webgl');

  if (!gl) return { renderer: 'Unknown', vendor: 'Unknown' };

  const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');

  return {
    renderer: debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : 'Unknown',
    vendor: debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : 'Unknown',
  };
}

const getPlugins = (): string => [...navigator.plugins].map((plugin) => plugin.name).join(', ');

const getDeviceMemory = (): number => {
  if ('deviceMemory' in navigator) return navigator.deviceMemory as number;

  return 0;
};

export const getUserFingerprintData = ({
  canvas_hash,
  instance_id,
  instance_type,
  battery,
}: IGetUserFingerprintDataDto): IUserFingerprintData => {
  const videoCardInformation = getVideoCardInfo();

  return {
    fingerprint_data: {
      available_screen_resolution: `${screen.width}x${screen.height}`,
      battery,
      canvas_hash,
      color_depth: screen.colorDepth,
      cookie_enabled: navigator.cookieEnabled,
      device_memory: getDeviceMemory(),
      do_not_track: navigator.doNotTrack === '1',
      hardware_concurrency: navigator.hardwareConcurrency,
      java_enabled: navigator.javaEnabled(),
      language: navigator.language,
      languages: navigator.languages.filter((lang) => lang.length === 2),
      platform: navigator.platform,
      plugins: getPlugins(),
      plugins_support: navigator.maxTouchPoints > 0,
      product: navigator.product,
      product_sub: navigator.productSub,
      screen_resolution: `${window.innerWidth}x${window.innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezone_offset: new Date().getTimezoneOffset(),
      touch_support: navigator.maxTouchPoints > 0,
      user_agent: navigator.userAgent,
      vendor: navigator.vendor,
      video_card_render: videoCardInformation.renderer,
      video_card_vendor: videoCardInformation.vendor,
    },
    instance_id,
    instance_type,
  };
};

export const getUserData = async ({ instance_id, instance_type }: IGetUserDataDto) => {
  const fingerprint = await load();
  const fingerprintResult = await fingerprint.get();
  const battery = await getBattery();

  const userFingerprintData = getUserFingerprintData({
    battery,
    canvas_hash: fingerprintResult.visitorId,
    instance_id,
    instance_type,
  });

  return userFingerprintData;
};

export interface IGetUserFingerprintDataDto {
  battery: boolean;
  canvas_hash: string;
  instance_id: string;
  instance_type: UserFingerprintDataInstanceType;
}
